/**
 * Zoho upload field options.
 * `dbColumn` must match the candidates table column name stored in user_profiles.zoho_upload_fields.
 */
export const ZOHO_UPLOAD_FIELDS = [
  { id: 'email', dbColumn: 'email', labelKey: 'connectAts.fields.email', defaultChecked: false },
  { id: 'current_employer', dbColumn: 'current_company', labelKey: 'connectAts.fields.currentEmployer', defaultChecked: true },
  { id: 'mobile', dbColumn: 'phone', labelKey: 'connectAts.fields.mobile', defaultChecked: false },
  { id: 'current_job_title', dbColumn: 'current_position', labelKey: 'connectAts.fields.currentJobTitle', defaultChecked: true },
  { id: 'skill_set', dbColumn: 'skills', labelKey: 'connectAts.fields.skillSet', defaultChecked: true },
  { id: 'candidate_name', dbColumn: 'full_name', labelKey: 'connectAts.fields.candidateName', defaultChecked: false },
  { id: 'city', dbColumn: 'city', labelKey: 'connectAts.fields.city', defaultChecked: true },
  { id: 'country', dbColumn: 'country', labelKey: 'connectAts.fields.country', defaultChecked: true },
  { id: 'notes_call', dbColumn: 'ai_summary', labelKey: 'connectAts.fields.notesCall', defaultChecked: true },
  { id: 'notes_meeting', dbColumn: 'notes_meeting', labelKey: 'connectAts.fields.notesMeeting', defaultChecked: true },
];

/** Legacy values previously stored in user_profiles.zoho_upload_fields. */
const DB_COLUMN_ALIASES = {
  current_title: 'current_position',
  summary: 'ai_summary',
};

const resolveDbColumn = (value) => {
  const key = String(value ?? '').trim();
  if (!key) return '';
  return DB_COLUMN_ALIASES[key] || key;
};

const FIELD_BY_DB_COLUMN = Object.fromEntries(
  ZOHO_UPLOAD_FIELDS.map((field) => [field.dbColumn, field]),
);

const FIELD_BY_ID = Object.fromEntries(
  ZOHO_UPLOAD_FIELDS.map((field) => [field.id, field]),
);

export const getDefaultZohoUploadDbColumns = () => (
  ZOHO_UPLOAD_FIELDS.filter((field) => field.defaultChecked).map((field) => field.dbColumn)
);

export const getDefaultUploadFieldsState = () => (
  Object.fromEntries(ZOHO_UPLOAD_FIELDS.map((field) => [field.id, field.defaultChecked]))
);

export const uploadFieldsStateFromDbColumns = (columns) => {
  const selected = new Set(
    (Array.isArray(columns) ? columns : [])
      .map(resolveDbColumn)
      .filter(Boolean),
  );

  return Object.fromEntries(
    ZOHO_UPLOAD_FIELDS.map((field) => [field.id, selected.has(field.dbColumn)]),
  );
};

export const dbColumnsFromUploadFieldsState = (fieldsState = {}) => (
  ZOHO_UPLOAD_FIELDS
    .filter((field) => Boolean(fieldsState?.[field.id]))
    .map((field) => field.dbColumn)
);

export const normalizeZohoUploadDbColumns = (columns) => {
  const seen = new Set();
  const normalized = [];

  for (const value of Array.isArray(columns) ? columns : []) {
    const key = resolveDbColumn(value);
    if (!key || !FIELD_BY_DB_COLUMN[key] || seen.has(key)) continue;
    seen.add(key);
    normalized.push(key);
  }

  return normalized;
};

export const hasZohoUploadFieldsConfigured = (columns) => (
  normalizeZohoUploadDbColumns(columns).length > 0
);

/** True when the profile has never saved a selection (null/undefined). */
export const isZohoUploadFieldsUnset = (columns) => columns == null;

/**
 * True when the user explicitly saved an empty selection.
 * Null/undefined (unset) is not considered empty.
 */
export const isZohoUploadFieldsEmpty = (columns) => (
  Array.isArray(columns) && normalizeZohoUploadDbColumns(columns).length === 0
);

export const hasSelectedUploadFields = (fieldsState = {}) => (
  dbColumnsFromUploadFieldsState(fieldsState).length > 0
);

export const getZohoUploadFieldById = (id) => FIELD_BY_ID[id] ?? null;
